import React, { useState, useContext } from "react";
import {
  UploadOutlined,
  BookOutlined,
  FormOutlined,
  EyeOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { Dropdown, Menu } from "antd";
import styled from "styled-components";
import UploadAssetDrawer from "Components/UploadAssetDrawer";
import {
  BASE_URL,
  GET_ASSETS_WITH_SM_ID,
  GET_CONTENT_LICENCE,
} from "utilFunctions/Constants/urlConstants";
import { UserInfoContext, ToastContextDispatch } from "Context/CommonContext";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { usePutPostAPI } from "utilFunctions/CustomHooks/usePutPostAPI";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import { useNavigate } from "react-router-dom";
import VideoPlayer from "Pages/AssetView/VideoPlayer";
import PDFPageviewer from "Pages/PlannerView/PDFPageviewer";
import ErrorView from "Pages/AssetView/ErrorView";
import CustomModal from "Components/CommonComponents/CustomModal";
import { UPLOAD_ASSET_LASTDATE } from "utilFunctions/Constants/Constants";
import { inBetweenTime } from "utilFunctions/shared/ScheduleStatus";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { DesktopOutlined } from "@ant-design/icons";
import { LectureData } from "./LectureTypes";

const StyledDropdown = styled(Dropdown)`
  .ant-dropdown-menu-item {
    padding: 15px !important;
  }
`;

interface QuickActionsCardProps {
  lecture?: LectureData;
}
interface ContentType {
  type: "video" | "ebook";
  data: string;
  client: string;
}

const QuickActionsCard: React.FC<QuickActionsCardProps> = ({
  lecture = {},
}) => {
  const { screen } = useWindowSize();
  const isMobile = ["xs", "sm"].includes(screen);
  const navigate = useNavigate();
  const ToastDispatch = useContext(ToastContextDispatch);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [assetViewOpen, setAssetViewOpen] = useState(false);
  const [contentType, setContentType] = useState<ContentType | null>(null);
  // const [pdfListOpen, setPdfListOpen] = useState(false);
  const [pdfPassword, setPdfPassword] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const { lectureAssetAccess } = userInfo || {};

  React.useEffect(() => {
    if (lecture?.lectureContent?.ebooks) {
      const arr = lecture.lectureContent.ebooks.map(
        (ele: any, index: number) => {
          const parts = ele?.url?.split("/");
          const fileName = parts?.[parts.length - 1];
          return {
            key: index + 1,
            label: fileName,
            url: ele?.url,
            client: ele?.client,
          };
        }
      );
      setItems(arr);
    }
  }, [lecture]);

  const { makeRequest: fetchFileDecryptionKey } = useGetApi(
    (data: any) => {
      if (data?.detailMessage) {
        const decodedKey = atob(data.detailMessage);
        setPdfPassword(decodedKey);
      } else {
        setPdfPassword("NO_PASSWORD");
      }
    },
    (error: any) => {
      setPdfPassword("NO_PASSWORD");
      console.error(error);
    }
  );

  const onClickViewPdf = ({ key }: any) => {
    setAssetViewOpen(true);
    const data = items.find((ele: any) => ele.key == key);
    let courseId = lecture?.courseId;
    if (data?.client === "CRYSTAL" && (!courseId || courseId == null)) {
      courseId = "000" as any;
    }
    fetchFileDecryptionKey(
      BASE_URL +
        GET_CONTENT_LICENCE +
        `?assetId=${lecture?.batchTimeTableId}&courseId=${courseId}&source=${data?.client}`
    );
    setContentType({
      type: "ebook",
      data: data?.url,
      client: data?.client,
    });
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleUploadSuccess = () => {
    setIsDrawerOpen(false);
  };

  const handleStartQuiz = () => {
    if (lecture?.batchTimeTableId) {
      const query = encodeURIComponent(JSON.stringify(lecture));
      window.open(
        `/admin/quiz/${lecture.batchTimeTableId}?data=${query}`,
        "_blank"
      );
    }
  };

  const handleViewLectureRecording = () => {
    const video = lecture?.lectureContent?.videos?.[0];
    if (!video) {
      ToastDispatch({
        isOpen: true,
        type: "Warning",
        message: "Lecture recording is not available.",
      });
      return;
    }

    setContentType({
      type: "video",
      data: video.url,
      client: video.client,
    });

    setAssetViewOpen(true);
  };

  // const ebooks: EbookAsset[] = lecture?.lectureContent?.ebooks || [];

  // const handleViewLecturePDF = () => {
  //   if (!ebooks.length) {
  //     ToastDispatch({
  //       isOpen: true,
  //       type: "Warning",
  //       message: "Lecture PDF is not available.",
  //     });
  //     return;
  //   }
  //   if (ebooks.length === 1) {
  //     setContentType({
  //       type: "pdf",
  //       data: ebooks[0].url,
  //       client: ebooks[0].client,
  //     });
  //     setAssetViewOpen(true);
  //   } else {
  //     setPdfListOpen(true);
  //   }
  // };

  // const handleSelectPDF = (ebook: EbookAsset) => {
  //   setContentType({
  //     type: "pdf",
  //     data: ebook.url,
  //     client: ebook.client,
  //   });
  //   setPdfListOpen(false);
  //   setAssetViewOpen(true);
  // };

  // const getPdfNameFromUrl = (url: string) => {
  //   const fileName = url.split("/").pop() || "Document.pdf";
  //   return decodeURIComponent(fileName.replace(/^\d+_/, ""));
  // };

  const detailShowHandler = (cardData: any) => {
    if (!cardData?.fexRecordId || !cardData?.courseId) {
      ToastDispatch({
        isOpen: true,
        type: "Warning",
        message: "Missing required information to show details.",
      });
      return;
    }
    const obj = {
      lectureId: cardData?.fexRecordId,
      showAssets: true,
      courseId: cardData?.courseId,
    };
    const url = BASE_URL + GET_ASSETS_WITH_SM_ID;
    GetAssets(url, obj);
  };

  const { makeRequest: GetAssets } = usePutPostAPI(
    (data: any) => {
      if (data && data["data"]) {
        navigate("/admin/mylibrary/" + data["data"]["smId"]);
      }
    },
    (error) => {
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message:
          error["response"]["data"]["internalCode"] ||
          error["response"]["data"]["message"],
      });
    },
    "Post"
  );

  const getLectureStatus = () => {
    if (inBetweenTime(lecture?.startTime, lecture?.endTime)) {
      return "ONGOING";
    }

    if (
      lecture?.syllabusStatus &&
      lecture.syllabusStatus.toUpperCase() === "NA"
    ) {
      return "";
    }

    return lecture?.syllabusStatus?.toUpperCase() || "";
  };

  const handleJoinMeeting = () => {
    if (!lecture?.eventLink) return;
    window.open(lecture.eventLink, "_blank");
  };

  const canPerformUpload =
    lectureAssetAccess && Object.keys(lectureAssetAccess).length > 0;
  const isUploadAllowedByStatus = !(
    lecture?.syllabusStatus === "Upcoming" ||
    new Date(lecture?.endTime ?? "") < new Date(UPLOAD_ASSET_LASTDATE) ||
    inBetweenTime(lecture?.startTime, lecture?.endTime) ||
    ["DEMO CLASS", "PTM", "ORIENTATION"].includes(lecture?.eventType ?? "")
  );

  const hasRecording =
    lecture?.lectureContent?.videos && lecture.lectureContent.videos.length > 0;

  const hasPdf =
    lecture?.lectureContent?.ebooks && lecture.lectureContent.ebooks.length > 0;

  const actions = [
    ...(isUploadAllowedByStatus && canPerformUpload
      ? [
          {
            id: "upload-notes",
            label: "Upload Notes",
            icon: (
              <UploadOutlined
                style={{ fontSize: "20px", color: "var(--primary-text)" }}
              />
            ),
            onClick: handleOpenDrawer,
          },
        ]
      : []),
    {
      id: "view-library",
      label: "View Library",
      icon: (
        <BookOutlined
          style={{ fontSize: "20px", color: "var(--primary-text)" }}
        />
      ),
      onClick: () => detailShowHandler(lecture),
    },
    {
      id: "quiz",
      label: "Quiz",
      icon: (
        <FormOutlined
          style={{ fontSize: "20px", color: "var(--primary-text)" }}
        />
      ),
      onClick: handleStartQuiz,
    },
    ...(hasRecording
      ? [
          {
            id: "view-recording",
            label: "View Recording",
            icon: (
              <EyeOutlined
                style={{ fontSize: 20, color: "var(--primary-text)" }}
              />
            ),
            onClick: handleViewLectureRecording,
          },
        ]
      : []),
    ...(hasPdf
      ? [
          {
            id: "view-lecture-pdf",
            label: "View Lecture PDF",
            icon: (
              <FilePdfOutlined
                style={{ fontSize: 20, color: "var(--primary-text)" }}
              />
            ),
            isDropdown: true,
          },
        ]
      : []),
  ];

  console.log("ebooks from lecture:", lecture?.lectureContent?.ebooks);
  console.log("items state:", items);

  return (
    <>
      <div
        className="quick-actions-card"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          padding: "20px",
        }}
      >
        {isMobile && (
          <>
            {getLectureStatus() && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 14px",
                  borderRadius: "999px",
                  background: "#DDF6E7",
                  color: "#1B7F45",
                  fontSize: "12px",
                  fontWeight: 700,
                  marginBottom: "16px",
                }}
              >
                ● {getLectureStatus()}
              </div>
            )}

            {lecture?.eventLink && (
              <button
                onClick={handleJoinMeeting}
                style={{
                  width: "100%",
                  height: "56px",
                  border: "none",
                  borderRadius: "16px",
                  background: "var(--primary-text)",
                  color: "#fff",
                  fontSize: "18px",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  marginBottom: "20px",
                  cursor: "pointer",
                }}
              >
                <DesktopOutlined />
                Join Meeting
              </button>
            )}
          </>
        )}
        <div
          style={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#94a3b8",
            letterSpacing: "0.05em",
            marginBottom: "16px",
          }}
        >
          QUICK ACTIONS
        </div>

        <div
          className="quick-action-grid"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? `repeat(${actions.length}, 1fr)`
              : "repeat(2, 1fr)",
            gap: "16px",
          }}
        >
          {actions.map((action) =>
            action.isDropdown ? (
              <StyledDropdown
                key={action.id}
                overlay={
                  <Menu
                    items={items}
                    onClick={onClickViewPdf}
                    style={{ padding: "12px" }}
                  />
                }
                placement="top"
              >
                <button
                  type="button"
                  onClick={(e) => e.preventDefault()}
                  className="quick-action-btn"
                  style={{ width: "100%", minHeight: "90px" }}
                >
                  {action.icon}
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "var(--primary-text)",
                      textAlign: "center",
                    }}
                  >
                    {action.label}
                  </span>
                </button>
              </StyledDropdown>
            ) : (
              <button
                key={action.id}
                type="button"
                onClick={action.onClick}
                className="quick-action-btn"
                style={{ width: "100%", minHeight: "90px" }}
              >
                {action.icon}
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "var(--primary-text)",
                    textAlign: "center",
                  }}
                >
                  {action.label}
                </span>
              </button>
            )
          )}
        </div>
      </div>
      <>
        <style>
          {`
            .ant-upload-drag-icon .anticon {
              color: var(--primary-text) !important;
            }
          `}
        </style>
        <UploadAssetDrawer
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          onUploadSuccess={handleUploadSuccess}
          BASE_URL={BASE_URL}
          lectureId={
            lecture?.batchTimeTableId
              ? String(lecture.batchTimeTableId)
              : undefined
          }
          courseId={lecture?.courseId ? String(lecture.courseId) : undefined}
          prevContent={lecture?.lectureContent}
          videoAccess={lectureAssetAccess?.videoAccess || []}
          pdfAccess={lectureAssetAccess?.pdfAccess || []}
        />
      </>

      <CustomModal
        modal={assetViewOpen}
        centered={true}
        backdrop={true}
        size={contentType?.type === "video" ? "lg" : "xl"}
        fullscreen={true}
        className={
          contentType?.type === "ebook"
            ? "document-viewer-fullscreen-modal"
            : undefined
        }
        title={
          contentType
            ? contentType.type === "video"
              ? `${lecture?.eventName} Video`
              : `${lecture?.eventName} PDF`
            : " "
        }
        toggle={() => setAssetViewOpen(false)}
      >
        <div
          className={
            contentType?.type === "ebook" ? "pdf-viewer-modal-content" : ""
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            padding: contentType?.type === "ebook" ? 0 : "10px",
            background: "#f8f9fa",
          }}
        >
          {contentType?.type === "video" && (
            <VideoPlayer
              videoUrl={contentType.data}
              JWTToken={""}
              assetId={lecture?.batchTimeTableId}
              source={contentType.client}
            />
          )}
          {contentType?.type === "ebook" &&
            (contentType?.data && pdfPassword != null ? (
              <PDFPageviewer
                allPageInOne={true}
                pdfUrl={contentType.data}
                pdfPassword={pdfPassword}
                isLibraryView={true}
                source={contentType.client}
                parentComponent={"AssetViewerModal"}
              />
            ) : (
              <ErrorView />
            ))}
        </div>
      </CustomModal>

      {/* <CustomModal
        modal={pdfListOpen}
        centered={true}
        backdrop={true}
        size={"md"}
        title="Lecture PDFs"
        toggle={() => setPdfListOpen(false)}
      >
        <div style={{ padding: "8px 4px" }}>
          {ebooks.map((ebook) => (
            <button
              key={ebook.id}
              type="button"
              onClick={() => handleSelectPDF(ebook)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "14px 16px",
                marginBottom: "8px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#fff",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <FilePdfOutlined
                style={{ fontSize: "20px", color: "var(--primary-text)" }}
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#212121",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getPdfNameFromUrl(ebook.url)}
              </span>
            </button>
          ))}
        </div>
      </CustomModal> */}
    </>
  );
};

export default QuickActionsCard;
